import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { palette } from '@openx/components-core/lib/theme/paletteV2';
import { abbreviateNumericValue } from '@openx/utils/lib/abbreviateNumericValue';
import type { ReactNode } from 'react';
import type { MetricListItem } from '../../data/chartTypes';

const ICON_SIZE = 18;
const ICON_CIRCLE_SIZE = 32;
const LOADING_ROW_KEYS = Array.from({ length: 7 }, (_, index) => `loading-row-${index}`);

type InsightsMetricListProps = {
  title: string;
  /** Unit of the listed values shown next to the title (e.g. "households") */
  unitLabel: string;
  items: MetricListItem[];
  isLoading?: boolean;
};

const HORIZONTAL_PADDING = 2.5;

const rowSx = {
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  gap: 1.5,
  px: HORIZONTAL_PADDING,
  py: 1,
  '&:nth-of-type(even)': { bgcolor: 'background.boxDark' },
};

// Thin scrollbar whose thumb is only painted while the list is hovered (the track stays reserved, so rows don't shift).
// Uses the standard `scrollbar-color` (Chromium 121+, Firefox): toggling it on `:hover` repaints reliably, unlike
// `::-webkit-scrollbar-thumb`, which Chromium doesn't repaint when only the parent's hover state changes.
// Browsers without `scrollbar-color` (Safari) get the always-visible DataTable-style webkit scrollbar instead.
const scrollbarSx = {
  '@supports (scrollbar-color: auto)': {
    scrollbarWidth: 'thin',
    scrollbarColor: 'transparent transparent',
    '&:hover': { scrollbarColor: `${palette.text.disabled} transparent` },
  },
  '@supports not (scrollbar-color: auto)': {
    '&::-webkit-scrollbar': { width: 8 },
    '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
    '&::-webkit-scrollbar-thumb': { backgroundColor: palette.text.disabled, borderRadius: 4 },
  },
};

const IconCircle = ({ children }: { children?: ReactNode }) => (
  <Box
    sx={{
      width: ICON_CIRCLE_SIZE,
      height: ICON_CIRCLE_SIZE,
      flexShrink: 0,
      borderRadius: '50%',
      bgcolor: 'primary.light',
      color: 'primary.main',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    {children}
  </Box>
);

export const InsightsMetricList = ({ title, unitLabel, items, isLoading = false }: InsightsMetricListProps) => {
  const renderRows = () => {
    if (isLoading) {
      return LOADING_ROW_KEYS.map(key => (
        <Box key={key} sx={rowSx} data-test="insights-metric-list-loading-row">
          <IconCircle />
          <Skeleton animation="wave" variant="text" sx={{ flex: 1 }} />
        </Box>
      ));
    }

    if (!items.length) {
      return (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body1" sx={{ color: 'text.secondary' }} data-test="insights-metric-list-no-data">
            No data available
          </Typography>
        </Box>
      );
    }

    return items.map(({ key, label, icon: Icon, value }) => (
      <Box key={key} sx={rowSx} data-test={`insights-metric-list-row-${key}`}>
        <IconCircle>
          <Icon size={ICON_SIZE} />
        </IconCircle>
        <Typography variant="body1" sx={{ flex: 1, minWidth: 0 }} noWrap>
          {label}
        </Typography>
        <Typography variant="body1Bold" data-test={`insights-metric-list-value-${key}`}>
          {abbreviateNumericValue(value, { allowZero: true, nullValue: 'N/A' })}
        </Typography>
      </Box>
    ));
  };

  return (
    <Box data-test="insights-metric-list" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 2,
          px: HORIZONTAL_PADDING,
          pt: HORIZONTAL_PADDING,
          pb: 1.5,
        }}
      >
        <Typography variant="h3" data-test="insights-metric-list-title">
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {unitLabel}
        </Typography>
      </Box>
      <Box
        data-test="insights-metric-list-rows"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          pb: 1,
          ...scrollbarSx,
        }}
      >
        {renderRows()}
      </Box>
    </Box>
  );
};
